import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, X, MapPin, MagnifyingGlass, ImageSquare, Pencil, ChatCircleDots, DownloadSimple, Upload } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import PhotoMarkupModal from "@/components/PhotoMarkupModal";

/**
 * QuickCaptureFab — the "CompanyCam-style" one-tap camera FAB.
 *
 * Sticks to the bottom-right of every logged-in page. Tap → capture
 * a photo (or import from library on desktop) → we pick a target WO
 * for it. GPS + reverse-address matching auto-select the closest
 * property so the photo lands on the right WO in one tap; falls back
 * to a manual picker if GPS is disabled or no match.
 *
 * Why we built this:
 *   Techs asked for parity with CompanyCam where "open app → snap →
 *   done" doesn't require navigating into a WO first. Solves the
 *   #1 friction point in daily photo capture.
 */
export default function QuickCaptureFab() {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);        // Blob to upload
  const [previewUrl, setPreviewUrl] = useState(null);
  const [gps, setGps] = useState(null);          // { lat, lng, accuracy }
  const [jobs, setJobs] = useState([]);
  const [query, setQuery] = useState("");
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [kind, setKind] = useState("before");    // before | after
  const [caption, setCaption] = useState("");    // what the photo is
  const cameraRef = useRef(null);
  const libraryRef = useRef(null);
  const nav = useNavigate();

  // ── Draggable FAB position ──
  // Techs on mobile were reporting the camera fab overlaps the SMS/chat
  // send button. Store its position as offsets from the bottom-right in
  // localStorage so once parked (e.g. bottom-left, or top-right), it
  // stays there across sessions. Long-press or drag to move.
  const POS_KEY = "quick_capture_fab_pos";
  const readPos = () => {
    try {
      const v = JSON.parse(localStorage.getItem(POS_KEY) || "null");
      if (v && Number.isFinite(v.right) && Number.isFinite(v.bottom)) return v;
    } catch { /* ignore */ }
    return { right: 20, bottom: 20 };
  };
  const [pos, setPos] = useState(readPos);
  const dragRef = useRef({ dragging: false, startX: 0, startY: 0, origRight: 0, origBottom: 0, moved: false });

  const clampPos = (right, bottom) => {
    const maxRight = Math.max(0, window.innerWidth - 64);
    const maxBottom = Math.max(0, window.innerHeight - 64);
    return {
      right: Math.min(maxRight, Math.max(0, right)),
      bottom: Math.min(maxBottom, Math.max(0, bottom)),
    };
  };
  useEffect(() => {
    const onResize = () => setPos(p => clampPos(p.right, p.bottom));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const onDragStart = (e) => {
    // Only enable drag with a long-press-ish gesture on mobile — a normal
    // tap should still open the sheet. Threshold is 6px of movement.
    const pt = e.touches?.[0] || e;
    dragRef.current = {
      dragging: true,
      startX: pt.clientX,
      startY: pt.clientY,
      origRight: pos.right,
      origBottom: pos.bottom,
      moved: false,
    };
    const onMove = (ev) => {
      const p = ev.touches?.[0] || ev;
      const dx = p.clientX - dragRef.current.startX;
      const dy = p.clientY - dragRef.current.startY;
      if (Math.abs(dx) < 6 && Math.abs(dy) < 6 && !dragRef.current.moved) return;
      dragRef.current.moved = true;
      ev.preventDefault?.();
      const next = clampPos(
        dragRef.current.origRight - dx,
        dragRef.current.origBottom - dy,
      );
      setPos(next);
    };
    const onEnd = () => {
      if (dragRef.current.moved) {
        try { localStorage.setItem(POS_KEY, JSON.stringify(pos)); } catch { /* ignore */ }
        // Consume the click that follows so the sheet doesn't open right
        // after a drag. We reset `moved` after the click event fires.
        setTimeout(() => { dragRef.current.moved = false; }, 200);
      }
      dragRef.current.dragging = false;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onEnd);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onEnd);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onEnd);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onEnd);
  };
  // Persist final position after each drag settles
  useEffect(() => {
    if (!dragRef.current.dragging) {
      try { localStorage.setItem(POS_KEY, JSON.stringify(pos)); } catch { /* ignore */ }
    }
  }, [pos]);

  // Cleanup blob URLs on close/unmount
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const openSheet = () => {
    setOpen(true);
    // Ask for GPS in the background; user can proceed without waiting
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
        () => setGps(null),
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 30000 }
      );
    }
    if (jobs.length === 0) loadJobs();
  };
  const close = () => {
    setOpen(false);
    setFile(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setQuery("");
    setCaption("");
  };

  const loadJobs = async () => {
    setLoadingJobs(true);
    try {
      // Pull the tech's currently-assigned WOs first (they're the
      // most likely target). Falls back to /jobs?limit=100 for admins.
      let list = [];
      try {
        const r = await api.get("/my/assignments");
        list = Array.isArray(r.data) ? r.data : (r.data?.jobs || []);
      } catch {}
      if (list.length < 5) {
        try {
          const r2 = await api.get("/jobs?limit=100");
          const more = Array.isArray(r2.data) ? r2.data : (r2.data?.jobs || r2.data?.items || []);
          // Merge unique by id
          const seen = new Set(list.map(j => j.id || j._id));
          for (const j of more) {
            const jid = j.id || j._id;
            if (!seen.has(jid)) { list.push(j); seen.add(jid); }
          }
        } catch {}
      }
      setJobs(list);
    } finally {
      setLoadingJobs(false);
    }
  };

  // Server-side search — the tech's assigned queue only holds the last
  // ~100 WOs, so a real WO like 3734-1 that's not on their plate would
  // never show up under a client-only filter. Debounce keeps this from
  // hammering the API on every keystroke.
  const [serverResults, setServerResults] = useState([]);
  const [serverSearching, setServerSearching] = useState(false);
  useEffect(() => {
    if (!open) return;
    // Merge the user's raw search query with any WO number pattern found
    // inside the caption they're typing (e.g. "leaking pipe on 3734-1")
    // so the exact WO surfaces as the top match without any extra tap.
    const captionWo = (caption || "").match(/\b\d{3,5}(?:-\d)?\b/);
    const q = (query.trim() || (captionWo?.[0] || "")).trim();
    if (q.length < 2) { setServerResults([]); return; }
    let alive = true;
    const t = setTimeout(async () => {
      setServerSearching(true);
      try {
        const r = await api.get("/jobs", { params: { q, limit: 25 } });
        const arr = Array.isArray(r.data) ? r.data : (r.data?.jobs || r.data?.items || []);
        if (alive) setServerResults(arr);
      } catch { if (alive) setServerResults([]); }
      finally { if (alive) setServerSearching(false); }
    }, 300);
    return () => { alive = false; clearTimeout(t); };
  }, [open, query, caption]);

  const onCameraPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(URL.createObjectURL(f));
  };

  // WO number typed inside the caption — surface as a pinned "Auto-detected"
  // match at the top of the list so the tech can attach in one tap.
  const captionWoMatch = (caption || "").match(/\b\d{3,5}(?:-\d)?\b/);
  const autoDetectHits = captionWoMatch
    ? serverResults.filter(j =>
        String(j.work_order_number || "").toLowerCase().includes(captionWoMatch[0].toLowerCase())
      ).slice(0, 3)
    : [];

  // Rank WOs by GPS-distance to current position, otherwise by recency.
  // Only address-strings with cached lat/lng qualify for the GPS rank —
  // the rest fall back to alphabetical/recent-first.
  const rankedJobs = (() => {
    const q = query.trim().toLowerCase();
    // Merge cached "my assignments" with fresh server results so a WO
    // like 3734-1 that isn't on the tech's plate still appears the
    // moment they type it.
    const merged = new Map();
    for (const j of jobs) merged.set(j.id || j._id, j);
    for (const j of serverResults) merged.set(j.id || j._id, j);
    let list = [...merged.values()];
    if (q) {
      list = list.filter(j =>
        (j.property_address || "").toLowerCase().includes(q) ||
        (j.title || "").toLowerCase().includes(q) ||
        (j.work_order_number || "").toString().toLowerCase().includes(q)
      );
    }
    if (gps && !q) {
      const dist = (j) => {
        const la = j.lat || j.property_lat;
        const ln = j.lng || j.property_lng;
        if (la == null || ln == null) return 1e12;
        const dLat = (la - gps.lat) * 111000;
        const dLng = (ln - gps.lng) * 111000 * Math.cos(gps.lat * Math.PI / 180);
        return Math.sqrt(dLat * dLat + dLng * dLng);
      };
      list = [...list].sort((a, b) => dist(a) - dist(b));
    }
    return list.slice(0, 40);
  })();

  const upload = async (jobId, jobLabel) => {
    if (!file || !jobId) return;
    // If they haven't described the photo yet and haven't scrolled through
    // the search results themselves, pop a quick confirm so we don't
    // silently attach a bathroom photo to WO 3712 because they mis-tapped
    // in a fast-scrolling list. Caption acts as intent — if they typed
    // one, we assume they meant this attach.
    if (!caption.trim()) {
      const label = jobLabel || "this WO";
      if (!window.confirm(`Attach photo to ${label}?`)) return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("kind", kind);
      fd.append("file", file, file.name || "photo.jpg");
      if (caption.trim()) fd.append("caption", caption.trim());
      await api.post(`/jobs/${jobId}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Photo attached${jobLabel ? ` to ${jobLabel}` : ""}`, {
        action: { label: "Open", onClick: () => nav(`/admin/jobs/${jobId}`) },
      });
      close();
    } catch (e) {
      toast.error(`Upload failed: ${e?.response?.data?.detail || "try again"}`);
    } finally {
      setUploading(false);
    }
  };

  // Escape hatch: tech can't find the WO (misspelled, closed, or the
  // office hasn't created it yet). Save the photo to the office review
  // queue so nobody's blocked in the field. Admin later attaches it.
  const saveOrphan = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file, file.name || "photo.jpg");
      fd.append("kind", kind);
      if (caption.trim()) fd.append("caption", caption.trim());
      if (query.trim()) fd.append("address_hint", query.trim());
      if (gps?.lat != null) fd.append("gps_lat", String(gps.lat));
      if (gps?.lng != null) fd.append("gps_lng", String(gps.lng));
      if (gps?.accuracy != null) fd.append("gps_accuracy", String(gps.accuracy));
      await api.post("/orphan-captures", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Saved to office queue — they'll attach it to the right WO", { duration: 6000 });
      close();
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || "try again"}`);
    } finally { setUploading(false); }
  };

  // Escape hatch #2: pipe the photo into #team chat so the crew sees it
  // instantly. Handy when the WO doesn't exist yet and someone needs to
  // spot-decide (e.g. "Should we bill this or eat it?").
  const sendToTeamChat = async () => {
    if (!file) return;
    setUploading(true);
    try {
      // Step 1: upload the file to team-chat storage
      const fd = new FormData();
      fd.append("files", file, file.name || "photo.jpg");
      if (gps?.lat != null) fd.append("lat", String(gps.lat));
      if (gps?.lng != null) fd.append("lng", String(gps.lng));
      if (gps?.accuracy != null) fd.append("accuracy", String(gps.accuracy));
      const up = await api.post("/team-chat/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const attachments = up?.data?.attachments || up?.data || [];
      // Step 2: post the message to #team
      const text = caption.trim() ||
        (query.trim() ? `📸 Photo · looking for ${query.trim()}` : "📸 Field photo");
      await api.post("/team-chat/channel/team/messages", {
        text,
        attachments,
        mention_props: [],
        mention_jobs: [],
      });
      toast.success("Sent to #team chat", { duration: 5000 });
      close();
    } catch (e) {
      toast.error(`Team chat send failed: ${e?.response?.data?.detail || "try again"}`);
    } finally { setUploading(false); }
  };

  // Escape hatch #3: just save it to the phone's photo library. The
  // browser doesn't have direct camera-roll write access, so this
  // triggers a download — iOS/Android both file that into Photos.
  const saveToPhone = () => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    const a = document.createElement("a");
    a.href = url;
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    const base = caption.trim().replace(/[^\w\s-]/g, "").slice(0, 40).trim().replace(/\s+/g, "_") || "field-photo";
    a.download = `${base}-${stamp}.jpg`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    toast.success("Saved — check your Downloads/Photos", { duration: 4000 });
  };

  return (
    <>
      {/* Floating action button — draggable so techs can park it out of the
          way of the SMS/chat send button on mobile. */}
      <button
        onClick={(e) => {
          // Swallow the click if we just finished dragging.
          if (dragRef.current.moved) { e.preventDefault(); e.stopPropagation(); return; }
          openSheet();
        }}
        onMouseDown={onDragStart}
        onTouchStart={onDragStart}
        data-testid="quick-capture-fab"
        title="Quick capture — snap a photo and attach to any WO (drag to move)"
        style={{ right: pos.right, bottom: pos.bottom, touchAction: "none" }}
        className="fixed z-[80] h-14 w-14 rounded-full bg-[#00C4CC] hover:bg-[#00e5ed] text-[#0F1F3A] shadow-2xl flex items-center justify-center transition-transform hover:scale-105 active:scale-95 cursor-grab active:cursor-grabbing">
        <Camera size={26} weight="fill"/>
      </button>

      {open && (
        <div className="fixed inset-0 z-[90] bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4"
             onClick={close} data-testid="quick-capture-sheet">
          <div className="w-full sm:max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92vh] flex flex-col"
               onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="p-4 border-b-2 border-slate-900 bg-slate-50 flex items-center gap-2">
              <Camera weight="fill" size={20} className="text-[#00C4CC]"/>
              <div className="font-display font-black text-base uppercase tracking-tight flex-1">
                Quick Capture
              </div>
              <button onClick={close} data-testid="quick-capture-close"
                className="h-9 w-9 flex items-center justify-center text-slate-500 hover:text-slate-800">
                <X size={20}/>
              </button>
            </div>

            {!file ? (
              /* Step 1: choose a photo */
              <div className="p-5 space-y-3">
                <p className="text-xs text-slate-600 font-medium">
                  Snap a photo or pick one from your library — we'll match it to the closest work order.
                </p>
                <button onClick={() => cameraRef.current?.click()}
                  data-testid="quick-capture-take"
                  className="w-full h-14 rounded-xl bg-[#00C4CC] hover:bg-[#00e5ed] text-[#0F1F3A] font-black uppercase tracking-widest text-sm inline-flex items-center justify-center gap-2">
                  <Camera size={20} weight="fill"/> Take Photo
                </button>
                <button onClick={() => libraryRef.current?.click()}
                  data-testid="quick-capture-library"
                  className="w-full h-12 rounded-xl bg-white border-2 border-slate-300 hover:border-slate-500 text-slate-700 font-black uppercase tracking-widest text-xs inline-flex items-center justify-center gap-2">
                  <ImageSquare size={18} weight="bold"/> Pick From Library
                </button>
                <input ref={cameraRef} type="file" accept="image/*" capture="environment"
                  onChange={onCameraPick} className="hidden" data-testid="quick-capture-camera-input"/>
                <input ref={libraryRef} type="file" accept="image/*"
                  onChange={onCameraPick} className="hidden" data-testid="quick-capture-library-input"/>
                {gps && (
                  <div className="text-[10px] text-slate-500 font-bold inline-flex items-center gap-1">
                    <MapPin size={10} weight="fill" className="text-[#00C4CC]"/>
                    Location pinned · nearest WO will be shown first
                  </div>
                )}
              </div>
            ) : (
              /* Step 2: pick target WO */
              <>
                <div className="p-4 space-y-3">
                  <div className="flex items-center gap-3">
                    <img src={previewUrl} alt="preview" className="h-16 w-16 rounded-lg object-cover border-2 border-slate-200"/>
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Attach as</div>
                      <div className="mt-1 inline-flex rounded-md overflow-hidden border-2 border-slate-300" data-testid="quick-capture-kind-toggle">
                        <button
                          onClick={() => setKind("before")}
                          data-testid="quick-capture-kind-before"
                          className={`h-8 px-3 text-[10px] font-black uppercase tracking-widest ${kind === "before" ? "bg-amber-500 text-white" : "bg-white text-slate-600"}`}>
                          Before
                        </button>
                        <button
                          onClick={() => setKind("after")}
                          data-testid="quick-capture-kind-after"
                          className={`h-8 px-3 text-[10px] font-black uppercase tracking-widest ${kind === "after" ? "bg-emerald-500 text-white" : "bg-white text-slate-600"}`}>
                          After
                        </button>
                      </div>
                    </div>
                    <button onClick={() => { setFile(null); if (previewUrl) URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }}
                      data-testid="quick-capture-retake"
                      className="text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-slate-800 px-2">
                      Retake
                    </button>
                  </div>
                  {/* Caption field — always visible so a photo of "leaky
                      pipe under kitchen sink" is never just a mystery
                      shot. Sent along with WO attach, orphan save, or
                      team chat post. */}
                  <input value={caption} onChange={(e) => setCaption(e.target.value)}
                    placeholder="What's this photo? (e.g. broken tile, kitchen sink leak)"
                    data-testid="quick-capture-caption"
                    className="w-full h-9 px-3 border-2 border-slate-300 rounded-md text-sm font-medium focus:border-cyan-500 focus:outline-none"/>
                  {/* Escape-hatch action strip — three ways to save a
                      photo without a WO. Techs are never blocked. */}
                  <div className="flex gap-2 flex-wrap" data-testid="quick-capture-escape-row">
                    <button onClick={sendToTeamChat} disabled={uploading}
                      data-testid="quick-capture-send-chat"
                      className="flex-1 min-w-[95px] h-9 px-2 rounded-md bg-white border-2 border-slate-300 hover:border-fuchsia-500 hover:text-fuchsia-700 text-slate-700 font-black uppercase tracking-widest text-[10px] inline-flex items-center justify-center gap-1 disabled:opacity-50">
                      <ChatCircleDots size={14} weight="fill"/> Team Chat
                    </button>
                    <button onClick={saveOrphan} disabled={uploading}
                      data-testid="quick-capture-save-orphan"
                      className="flex-1 min-w-[95px] h-9 px-2 rounded-md bg-white border-2 border-slate-300 hover:border-amber-500 hover:text-amber-700 text-slate-700 font-black uppercase tracking-widest text-[10px] inline-flex items-center justify-center gap-1 disabled:opacity-50">
                      <Upload size={14} weight="bold"/> Office Queue
                    </button>
                    <button onClick={saveToPhone} disabled={uploading}
                      data-testid="quick-capture-save-phone"
                      className="flex-1 min-w-[95px] h-9 px-2 rounded-md bg-white border-2 border-slate-300 hover:border-slate-500 hover:text-slate-900 text-slate-700 font-black uppercase tracking-widest text-[10px] inline-flex items-center justify-center gap-1 disabled:opacity-50">
                      <DownloadSimple size={14} weight="bold"/> Phone
                    </button>
                  </div>
                  <div className="relative">
                    <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                    <input value={query} onChange={(e) => setQuery(e.target.value)}
                      placeholder="Search WO or address…"
                      data-testid="quick-capture-search"
                      className="w-full h-10 pl-9 pr-3 border-2 border-slate-300 rounded-md text-sm font-medium focus:border-cyan-500 focus:outline-none"/>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto divide-y divide-slate-100" data-testid="quick-capture-job-list">
                  {/* Auto-detected WO in caption — pinned match */}
                  {autoDetectHits.length > 0 && (
                    <div data-testid="quick-capture-auto-detect" className="bg-amber-50/70 border-l-4 border-amber-500">
                      <div className="px-4 pt-2 pb-1 text-[9px] font-black uppercase tracking-widest text-amber-800">
                        ✨ Detected in caption: WO {captionWoMatch[0]}
                      </div>
                      {autoDetectHits.map(j => {
                        const jid = j.id || j._id;
                        const label = `${j.work_order_number ? `WO ${j.work_order_number} · ` : ""}${j.property_address || j.title || "WO"}`.slice(0, 60);
                        return (
                          <button key={`ad-${jid}`}
                            onClick={() => upload(jid, label)}
                            disabled={uploading}
                            data-testid={`quick-capture-autodetect-${(jid || "").slice(-8)}`}
                            className="w-full px-4 py-3 flex items-start gap-3 text-left hover:bg-amber-100 active:bg-amber-200 disabled:opacity-50">
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-black text-slate-900 truncate">
                                {j.work_order_number ? `WO-${j.work_order_number} · ` : ""}{j.title || "Untitled"}
                              </div>
                              <div className="text-[11px] text-slate-500 font-bold truncate">{j.property_address || ""}{j.unit ? ` · #${j.unit}` : ""}</div>
                            </div>
                            <span className="flex-shrink-0 h-8 px-3 rounded bg-amber-500 text-white font-black uppercase tracking-widest text-[10px] inline-flex items-center">
                              Attach →
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {loadingJobs && (
                    <div className="p-6 text-center text-xs font-black uppercase tracking-widest text-slate-400">
                      Loading work orders…
                    </div>
                  )}
                  {!loadingJobs && rankedJobs.length === 0 && (
                    <div className="p-4 text-center space-y-2" data-testid="quick-capture-empty">
                      <div className="text-xs font-black uppercase tracking-widest text-slate-500">
                        {serverSearching ? "Searching…" : "No matching work orders"}
                      </div>
                      {!serverSearching && query.trim() && (
                        <div className="text-[11px] text-slate-500 font-medium leading-snug">
                          Try a partial WO number or address — or use the buttons above to send it to <b>Team Chat</b>, save to the <b>Office Queue</b>, or download to your <b>Phone</b>.
                        </div>
                      )}
                    </div>
                  )}
                  {rankedJobs.map(j => {
                    const jid = j.id || j._id;
                    const label = `${j.work_order_number ? `WO ${j.work_order_number} · ` : ""}${j.property_address || j.title || "WO"}`.slice(0, 60);
                    return (
                      <button key={jid}
                        onClick={() => upload(jid, label)}
                        disabled={uploading}
                        data-testid={`quick-capture-target-${(jid || "").slice(-8)}`}
                        className="w-full px-4 py-3 flex items-start gap-3 text-left hover:bg-cyan-50 active:bg-cyan-100 disabled:opacity-50 border-b border-slate-100">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-black text-slate-900 truncate">
                            {j.work_order_number ? `WO-${j.work_order_number} · ` : ""}{j.title || j.description || "Untitled"}
                          </div>
                          <div className="text-[11px] text-slate-500 font-bold truncate">{j.property_address || ""}{j.unit ? ` · #${j.unit}` : ""}</div>
                        </div>
                        <span className="flex-shrink-0 h-8 px-3 rounded bg-cyan-100 text-cyan-800 font-black uppercase tracking-widest text-[10px] inline-flex items-center">
                          Attach →
                        </span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
